import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import GqlClientContext from '../../../contexts/GqlClientContext';
import useFilterInput from '../../../hooks/useFilterInput';
import getGroupVsRemainder from '../getGroupVsRemainder';
import useDateScope from '../../../hooks/useDateScope';
import useLockedDebouncedEffect from '../../../hooks/useLockedDebouncedEffect';
import { GroupVsRemainder } from '../types';
import filterTypeCheckers from '../../FilterPlateForm/filterTypeCheckers';
import { GroupVsRemainderContext } from '../GroupVsRemainderProvider';
import relativeDateRangeTypeCheckers from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider/relativeDateRangeTypeCheckers';
import DatasetDefinitionsContext from '../../../contexts/DatasetDefinitionsContext';
import fiToDrillDowns from '../../../screens/DataManager/Metrics/MetricPopup/MetricForm/NormalMetricForm/fiToDrillDowns';

const useGroupVsRemainder = ({
  groupVsRemainder,
}: {
  groupVsRemainder: VisualisationDefinitions.GroupVsRemainder;
}) => {
  const { setPeerGroupValues } = useContext(GroupVsRemainderContext);
  const { client } = useContext(GqlClientContext);
  const { datasets } = useContext(DatasetDefinitionsContext);
  const dateScope = useDateScope({});
  const { filterInput, lastDrilldown } = useFilterInput({
    extractEntityFilter: true,
    entityFilterAsScope: groupVsRemainder.useEntityAsScope,
  });

  const [data, setData] = useState<GroupVsRemainder.SeriesItem[] | undefined>();
  const [args, setArgs] = useState<
    GroupVsRemainder.RequestArguments | undefined
  >();

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const lastDrilldownTextFilter = useMemo(() => {
    if (!lastDrilldown) {
      return false;
    }

    const drilldownAsFixedFilters = fiToDrillDowns(lastDrilldown, datasets);
    return drilldownAsFixedFilters.find((df) =>
      filterTypeCheckers.isTextFilter(df),
    );
  }, [datasets, lastDrilldown]);

  const compatibleComparison = useMemo(() => {
    if (!lastDrilldown) {
      return true;
    }

    return !!lastDrilldownTextFilter;
  }, [lastDrilldownTextFilter, lastDrilldown]);

  const selectedEntities = useMemo((): FilterInput | undefined => {
    if (!lastDrilldownTextFilter) {
      return undefined;
    }
    return lastDrilldown;
  }, [lastDrilldownTextFilter, lastDrilldown]);

  // If drilldowns are being used, take the last one as selected entities and remove from filterInput
  useEffect(() => {
    if (lastDrilldownTextFilter) {
      groupVsRemainder.selectedEntities = lastDrilldown;
    } else {
      groupVsRemainder.selectedEntities = undefined;
    }
  }, [
    lastDrilldown,
    filterInput,
    filterInput.keywords,
    groupVsRemainder,
    datasets,
    lastDrilldownTextFilter,
  ]);

  useEffect(() => {
    const dateInterval =
      relativeDateRangeTypeCheckers.isCalendarWithAutoInterval(
        groupVsRemainder.trendByCalendarInterval,
      )
        ? groupVsRemainder.trendByCalendarInterval
        : undefined;
    const fiscalInterval = relativeDateRangeTypeCheckers.isFiscalInterval(
      groupVsRemainder.trendByCalendarInterval,
    )
      ? groupVsRemainder.trendByCalendarInterval
      : undefined;

    setArgs({
      filterInput,
      peerGroupField: groupVsRemainder.peerGroup,
      dateScope,
      dateInterval,
      fiscalInterval,
      dataType: groupVsRemainder.dataset,
      field: groupVsRemainder.field,
      selectedEntities,
      client,
    });
  }, [
    client,
    filterInput,
    dateScope,
    groupVsRemainder.dataset,
    groupVsRemainder.field,
    groupVsRemainder.peerGroup,
    groupVsRemainder.selectedEntities,
    groupVsRemainder.trendByCalendarInterval,
    selectedEntities,
  ]);

  const responseHandler = useCallback(
    (response: GroupVsRemainder.Response) => {
      const chartData: GroupVsRemainder.SeriesItem[] = response.histogram.map(
        (item) => ({
          group: item.selected,
          remainder: item.remainder,
          endDate: item.endDate,
          startDate: item.startDate,
          peerGroupValues: item.peerGroupValues,
        }),
      );
      if (response.histogram[0]) {
        setPeerGroupValues(response.histogram[0].peerGroupValues);
      }

      setData(chartData);
      setIsLoading(false);
    },
    [setPeerGroupValues],
  );

  useLockedDebouncedEffect({
    args,
    callback: getGroupVsRemainder,
    responseHandler,
    // A gadget's read: try a failed request once more
    shouldRetryOnce: true,
  });

  return {
    data,
    isLoading,
    dateScope,
    compatibleComparison,
  };
};

export default useGroupVsRemainder;
